<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { ModuleInterface } from '~~/composables/useModules';
    import { FEATURE_FLAG } from '~/app/enums/FeatureFlag';
    import { AI_ASSISTANT_MODAL_NAME } from '~/stores/aiAssistantStore';
    import { useEvent } from '~/composables/useEventBus';
    import { useProfileReviewStore } from '~/stores/profileReviewStore';

    const props = defineProps<{
        modules: ModuleInterface[];
        collapsed: boolean;
        isBurger?: boolean;
        showAssistant?: boolean;
        // Mobile drawer: tapping a module with children expands its submenu
        // inline instead of navigating, so the user picks the page directly.
        mobileExpand?: boolean;
    }>();

    const featureFlag = useFeatureFlag();
    const { isUpsellRestricted, isCrossFamily, isMenuEntryDenied } = useUpsellAccess();
    const { t } = useTranslation();

    // Reactive visibility gate, complementing the `v-can` directive. `v-can` is functional
    // (runs once at mount), so an entry denied by the async menu-visibility map — e.g. a
    // `user` without Messages/Visibility rights — stayed visible after the map resolved,
    // the flash Simon reported (TDD-3522). This recomputes when the store loads.
    const isModuleHidden = (mod: ModuleInterface): boolean => {
        if (!mod.checkPermission) return false;
        return isMenuEntryDenied(mod.permissionKey ?? `${mod.key}:access`);
    };

    // Unread marketplace conversations — drives the red badge on the "Messages" nav item
    // (spec §8.1 AC9). Kept live app-wide by useMessagesUnreadInit() (layouts/base.vue).
    const { count: messagesUnreadCount } = storeToRefs(useMessagesUnreadStore());
    const messagesUnreadLabel = computed<string>(() => (messagesUnreadCount.value > 9 ? '9+' : String(messagesUnreadCount.value)));

    const profileReviewStore = useProfileReviewStore();

    // Unpaid status (TDD-5601/TDD-5603): count badge on "Paramètres" (Mes factures lives
    // under it), and at level 4 every module is disabled except settings — the menu stays
    // visible so the client still sees what's inside the software, but cannot navigate.
    const { invoicesCount: unpaidInvoicesCount, hasUnpaidInvoices, isBlocked: isUnpaidBlocked } = useUnpaidStatus();
    const unpaidCountLabel = computed<string>(() => (unpaidInvoicesCount.value > 9 ? '9+' : String(unpaidInvoicesCount.value)));
    const isModuleLockedByUnpaid = (mod: ModuleInterface): boolean => isUnpaidBlocked.value && mod.key !== 'settings';
    const { track: unpaidTrack } = useUnpaidTracking();

    const openAssistant = () => {
        useEvent('modal:open', AI_ASSISTANT_MODAL_NAME);
    };

    const stripTrailingSlash = (path: string): string => path.replace(/\/+$/, '') || '/';

    // Accordion state for mobile-expand mode, pre-opened on the current module.
    const expandedKey = ref<string | null>(props.modules.find((mod) => mod.current)?.key ?? null);

    // A module may declare `children` purely so the route-upsell guard protects its per-tab deep links,
    // while opting OUT of the sidebar sub-nav via `hideSubmenu` (TDM-855 — Visibility uses in-page tabs, so it
    // is a single entry point). Such a module shows no chevron and no submenu, and navigates directly.
    const showsSubmenu = (mod: ModuleInterface): boolean => !!mod.children && mod.children.length > 0 && !mod.hideSubmenu;

    const isSubmenuOpen = (mod: ModuleInterface): boolean => {
        if (!showsSubmenu(mod)) return false;
        return props.mobileExpand ? expandedKey.value === mod.key : mod.current;
    };

    const handleModuleClick = (e: Event, mod: ModuleInterface) => {
        // Blocked tenant (unpaid level 4): the entry is visible but inert.
        if (isModuleLockedByUnpaid(mod)) {
            e.preventDefault();
            unpaidTrack('unpaid_locked_item_clicked', { module: mod.key });
            return;
        }

        const permKey = mod.permissionKey ?? `${mod.key}:access`;
        if (isUpsellRestricted(permKey)) {
            e.preventDefault();
            // The modal tracks the upsell exposure on open (type: 'nav', label: module name).
            useEvent('upsell:modal:open', { permissionKey: permKey, type: 'nav', label: t(`modules.${mod.key}`), crossFamily: isCrossFamily(permKey) });
            return;
        }

        // Mobile drawer: expand/collapse the submenu instead of navigating.
        if (props.mobileExpand && showsSubmenu(mod)) {
            e.preventDefault();
            expandedKey.value = expandedKey.value === mod.key ? null : mod.key;
            return;
        }

        // Already on this module's page → clicking its nav item must not re-navigate (avoids a
        // redundant route change/reload). Let the page react instead — e.g. Messages clears the
        // open conversation back to its "select a conversation" empty state (TDM-755).
        const targetPath = mod.basePath ?? `/${mod.key}/${mod.defaultPath()}`;
        if (stripTrailingSlash(useRoute().path) === stripTrailingSlash(targetPath)) {
            e.preventDefault();
            useEvent('app:nav:reselected', mod.key);
        }
    };
</script>

<template>
    <ul class="flex flex-col pl-2 py-0.5 gap-0.5 mt-4">
        <li
            v-if="showAssistant && featureFlag.isEnabled(FEATURE_FLAG.AI_ASSISTANT)"
            class="relative"
        >
            <button
                type="button"
                class="relative flex transition-all rounded-md pl-2 pr-3 py-1.5 text-left"
                :class="[collapsed ? 'w-10' : 'w-full', 'md:hover:bg-branding-rich-black']"
                @click="openAssistant"
            >
                <div
                    class="text-xl gap-3 flex flex-row items-center w-full"
                    :class="isBurger ? 'text-black' : 'text-white'"
                >
                    <i
                        class="fa-regular fa-sparkles text-base w-6.5 text-center flex-shrink-0"
                        aria-hidden="true"
                    />
                    <transition
                        enter-active-class="transition-all duration-100 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-all duration-100 ease-out delay-150"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <span
                            v-if="!collapsed"
                            class="text-sm font-inter text-nowrap max-md:font-medium"
                        >
                            {{ $t('ai.sidebar.title') }}
                        </span>
                    </transition>
                </div>
            </button>
        </li>
        <li
            v-for="availableModule in modules"
            v-show="!isModuleHidden(availableModule)"
            :key="availableModule.key"
            v-can="availableModule.checkPermission ? availableModule.permissionKey ?? `${availableModule.key}:access` : null"
            class="relative"
        >
            <NuxtLink
                :to="availableModule.basePath ?? `/${availableModule.key}/${availableModule.defaultPath()}`"
                class="relative flex transition-all rounded-md pl-2 pr-3 py-1.5"
                :aria-current="availableModule.current ? 'page' : undefined"
                :aria-disabled="isModuleLockedByUnpaid(availableModule) || undefined"
                :class="[
                    collapsed ? 'w-10' : 'w-full',
                    {
                        'bg-branding-white/10 hover:bg-branding-white/10 md:hover:bg-branding-rich-black max-md:bg-branding-simple-grey/40':
                            availableModule.current && !isModuleLockedByUnpaid(availableModule),
                        'md:hover:bg-branding-rich-black': !availableModule.current && !isModuleLockedByUnpaid(availableModule),
                        'opacity-40 cursor-not-allowed': isModuleLockedByUnpaid(availableModule),
                    },
                ]"
                @click.capture="(e: Event) => handleModuleClick(e, availableModule)"
            >
                <div
                    class="text-xl gap-3 flex flex-row items-center w-full"
                    :class="isBurger ? 'text-black' : 'text-white'"
                >
                    <i
                        class="fa-jelly fa-regular text-base w-6.5 text-center flex-shrink-0"
                        :class="[`fa-${availableModule.icon}`, availableModule.current ? 'fa-jelly-fill' : '']"
                        aria-hidden="true"
                    />
                    <transition
                        enter-active-class="transition-all duration-100 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-all duration-100 ease-out delay-150"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <span
                            v-if="!collapsed"
                            class="text-sm font-inter text-nowrap max-md:font-medium"
                        >
                            {{ $t(`modules.${availableModule.key}`) }}
                        </span>
                    </transition>
                    <transition
                        enter-active-class="transition-all duration-100 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-all duration-100 ease-out delay-150"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <CommonUpsellBadge
                            v-if="!collapsed && availableModule.permissionKey && isUpsellRestricted(availableModule.permissionKey)"
                            variant="lightning"
                            class="ml-auto"
                        />
                        <span
                            v-else-if="!collapsed && availableModule.key === 'messages' && messagesUnreadCount > 0"
                            class="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-statistics-red px-1.5 text-xs font-semibold leading-none text-white"
                            :aria-label="$t('messages.nav.unread_badge', { count: messagesUnreadCount })"
                        >
                            {{ messagesUnreadLabel }}
                        </span>
                        <span
                            v-else-if="!collapsed && availableModule.key === 'settings' && hasUnpaidInvoices"
                            class="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-statistics-red px-1.5 text-xs font-semibold leading-none text-white"
                            :aria-label="$t('settings.subscription.unpaid.nav.badge', { count: unpaidInvoicesCount })"
                        >
                            {{ unpaidCountLabel }}
                        </span>
                        <span
                            v-else-if="!collapsed && availableModule.key === 'visibility' && profileReviewStore.needsAttention"
                            class="ml-auto size-2.5 shrink-0 rounded-full border border-branding-brand-yellow bg-branding-brand-yellow-fill"
                            :aria-label="$t('profile_review.dot_label')"
                        ></span>
                        <i
                            v-else-if="!collapsed && showsSubmenu(availableModule)"
                            class="ml-auto text-xs transition duration-100 fa-chevron-down text-neutral-400 fa-solid dark:text-neutral-300"
                            :class="{ 'rotate-180': mobileExpand && isSubmenuOpen(availableModule) }"
                        />
                    </transition>
                </div>
            </NuxtLink>

            <!-- Collapsed sidebar: the unread count can't fit next to a hidden label, so surface a
                 red dot on the "Messages" icon instead (spec §8.1 AC9). -->
            <span
                v-if="collapsed && availableModule.key === 'messages' && messagesUnreadCount > 0"
                class="pointer-events-none absolute left-6 top-1 size-2.5 rounded-full bg-statistics-red ring-2 ring-branding-rich-black"
                aria-hidden="true"
            ></span>

            <!-- Collapsed sidebar: same red-dot treatment for the unpaid count on "Paramètres". -->
            <span
                v-if="collapsed && availableModule.key === 'settings' && hasUnpaidInvoices"
                class="pointer-events-none absolute left-6 top-1 size-2.5 rounded-full bg-statistics-red ring-2 ring-branding-rich-black"
                aria-hidden="true"
            ></span>

            <span
                v-if="collapsed && availableModule.key === 'visibility' && profileReviewStore.needsAttention"
                class="pointer-events-none absolute left-6 top-1 size-2.5 rounded-full border border-branding-brand-yellow bg-branding-brand-yellow-fill ring-2 ring-branding-rich-black"
                aria-hidden="true"
            ></span>

            <transition
                enter-active-class="transition-all duration-1000 ease-in-out"
                enter-from-class="max-h-0 opacity-0"
                enter-to-class="max-h-screen opacity-100"
                leave-active-class="transition-all duration-500 ease-in-out"
                leave-from-class="max-h-screen opacity-100"
                leave-to-class="max-h-0 opacity-0"
            >
                <div
                    v-if="!collapsed && isSubmenuOpen(availableModule)"
                    class="sidebar-nav__submenu-container"
                >
                    <LayoutSidebarSubmenu
                        :content="availableModule.children"
                        :class="isBurger ? 'text-black' : 'text-white'"
                    />
                </div>
            </transition>
        </li>
    </ul>
</template>

<style lang="scss" scoped>
    .sidebar-nav__submenu-container {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            top: 4px;
            left: 22px;
            width: 1px;
            height: 100%;
            background-color: rgba(255, 255, 255, 0.4);
        }
    }
</style>
